import * as React from 'react';
import styled from 'styled-components';

enum ButtonVariants {
  primary = 'primary',
  secondary = 'secondary',
  default = 'default',
  text = 'text',
}
type ButtonVariant = keyof typeof ButtonVariants;

enum ButtonSizes {
  small = 'small',
  medium = 'medium',
  large = 'large',
  xLarge = 'xLarge',
}
type ButtonSize = keyof typeof ButtonSizes;

type ButtonStyleProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
};
export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  ButtonStyleProps & {
    onClick?(e: React.MouseEvent<HTMLButtonElement>): void;
    disabled?: boolean;
    startIcon?: React.ReactNode;
    endIcon?: React.ReactNode;
    as?: React.ElementType;
    style?: Record<string, string | number>;
    uppercase?: boolean;
  };

const Label = styled.span<Pick<ButtonProps, 'uppercase'>>((props) => ({
  width: '100%', // Ensure the correct width for iOS Safari
  display: 'inherit',
  alignItems: 'inherit',
  justifyContent: 'inherit',
  ...(props.uppercase && { textTransform: 'uppercase' }),
}));

const Span = styled.span<{ position: 'start' | 'end' }>(({ position }) => ({
  display: 'inherit',
  marginRight: position === 'start' ? 8 : -4,
  marginLeft: position === 'start' ? -4 : 8,
}));

export const ButtonRoot = styled.button<
  ButtonStyleProps & Pick<ButtonProps, 'disabled'>
>((props) => {
  const { theme, disabled, size, fullWidth, variant } = props;

  return {
    fontFamily: theme.fontFamily,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    fontWeight: theme.medium,
    borderRadius: '2px',
    cursor: 'pointer',
    userSelect: 'none',
    verticalAlign: 'middle',
    padding: '4px',
    margin: 0,
    width: fullWidth ? '100%' : 'auto',
    transition: `all ${theme.transitionEasing}`,
    textDecoration: 'none',
    outline: 'none',
    border: 'none',
    '-moz-appearance': 'none', // Reset
    '-webkit-appearance': 'none', // Reset

    '&::-moz-focus-inner': {
      borderStyle: 'none', // Remove Firefox dotted outline.
    },

    ...(disabled && { cursor: 'default', pointerEvents: 'none' }),

    // sizes
    ...(size === 'small' && {
      padding: theme.spacing(0.5, 2),
      fontSize: 12,
      lineHeight: '16px',
      height: 24,
    }),
    ...(size === 'medium' && {
      padding: theme.spacing(1, 2),
      fontSize: 14,
      lineHeight: '18px',
      height: 32,
    }),
    ...(size === 'large' && {
      padding: theme.spacing(1.5, 2),
      fontSize: 14,
      lineHeight: '18px',
      height: 40,
    }),
    ...(size === 'xLarge' && {
      // NOTE: update padding once Stefan has added in Figma
      padding: theme.spacing(1.75, 4),
      fontSize: 16,
      lineHeight: '20px',
      height: 48,
    }),

    // variants
    ...(variant === 'primary' && {
      color: theme.white,
      background: theme.gradients.main,
      backgroundPosition: 'top left',
      backgroundSize: '100% 200%',
      '&:hover': {
        backgroundPosition: disabled ? 'top left' : '0% 100%',
        '@media (hover:none)': {
          backgroundPosition: 'top left',
        },
      },

      '&:disabled': {
        color: theme.hex2rgba(theme.white, 0.6),
        cursor: 'not-allowed',
      },

      ...(size === 'xLarge' && {
        position: 'relative',
        overflow: 'hidden',

        [Span]: {
          margin: 0,
          position: 'absolute',
          top: 0,
          bottom: 0,
          width: 48,
          height: 48,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(180deg, ${theme.blue08} 0%, ${theme.blue09} 100%)`,

          '&.start-icon': {
            left: 0,
          },

          '&.end-icon': {
            right: 0,
          },
        },
      }),
    }),
    ...(variant === 'secondary' && {
      color: theme.blue07,
      backgroundColor: theme.white,
      border: `1px solid ${theme.blue07}`,
      '&:hover': {
        backgroundColor: disabled ? theme.white : theme.blue01,
        '@media (hover:none)': {
          backgroundColor: theme.white,
        },
      },
    }),
    ...(variant === 'default' && {
      color: theme.white,
      backgroundColor: theme.blue15,
      '&:hover': {
        backgroundColor: disabled ? theme.blue15 : theme.blue14,
        '@media (hover:none)': {
          backgroundColor: theme.blue15,
        },
      },
    }),
    ...(variant === 'text' && {
      color: theme.hex2rgba(theme.white, 0.8),
      backgroundColor: 'transparent',
      fontWeight: theme.regular,
      '&:hover': {
        color: disabled ? theme.hex2rgba(theme.white, 0.8) : theme.white,
        '@media (hover:none)': {
          color: theme.white,
        },
      },
    }),
  };
});

const Button = React.forwardRef<
  HTMLButtonElement,
  React.PropsWithChildren<ButtonProps>
>(function Button(props, ref) {
  const {
    children,
    onClick,
    disabled = false,
    uppercase = false,
    variant = 'default',
    size = 'medium',
    startIcon,
    endIcon,
    ...other
  } = props;

  return (
    <ButtonRoot
      ref={ref}
      onClick={onClick}
      disabled={disabled}
      variant={variant}
      size={size}
      {...other}
    >
      {/*
       * The inner Label (<span>) is required to vertically align the children.
       * Browsers don't support `display: flex` on a <button> element.
       * https://github.com/philipwalton/flexbugs/blob/master/README.md#flexbug-9
       */}
      <Label uppercase={uppercase}>
        {startIcon && (
          <Span className="start-icon" position="start">
            {startIcon}
          </Span>
        )}
        {children}
        {endIcon && (
          <Span className="end-icon" position="end">
            {endIcon}
          </Span>
        )}
      </Label>
    </ButtonRoot>
  );
});

export default Button;
